<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="modalTitle"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !canSave"
      :save-fn="handleSubmit"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <despesas-categorias-form
        ref="formRef"
        :is-edit-mode="isEditMode"
        :categoria="categoria">
      </despesas-categorias-form>
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;
type ModalMode = 'create' | 'edit';

interface Categoria {
  despesa_categoria_id: Id;
  [key: string]: unknown;
}

interface OpenModalData {
  mode?: ModalMode;
  categoria?: Categoria | null;
}

interface FormInstance {
  saveCategoria: () => Promise<void>;
  canSave: boolean;
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const mode = ref<ModalMode>('create');
const categoria = ref<Categoria | null>(null);

const formRef = ref<FormInstance | null>(null);

const modalTitle = computed<string>(() => {
  return mode.value === 'create' ? t('Nova Categoria') : t('Editar Categoria');
});

const isEditMode = computed<boolean>(() => mode.value === 'edit');

const canSave = computed<boolean>(() => {
  return formRef.value?.canSave || false;
});

const handleClose = (): void => {
  log('DespesasCategoriasModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  mode.value = 'create';
  categoria.value = null;
};

const errMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const handleSubmit = async (): Promise<void> => {
  if (!formRef.value) {
    log('DespesasCategoriasModal', 'handleSubmit', 'Form ref not available');
    return;
  }

  log('DespesasCategoriasModal', 'handleSubmit', 'Calling form saveCategoria');

  try {
    await formRef.value.saveCategoria();
    log('DespesasCategoriasModal', 'handleSubmit', 'Form submitted successfully');
    eventBus.emit('despesas-categorias.saved');
    handleClose();
  } catch (error) {
    log('DespesasCategoriasModal', 'handleSubmit', 'Error submitting form', error);
    const errorMessage = mode.value === 'create'
      ? t('Erro ao criar categoria')
      : t('Erro ao atualizar categoria');
    eventBus.emit('globalMessage.error', errorMessage + ': ' + errMessage(error, ''));
  }
};

const handleOpen = (data?: OpenModalData): void => {
  log('DespesasCategoriasModal', 'handleOpen', 'Opening modal via eventBus', data);
  mode.value = data?.mode || 'create';
  categoria.value = data?.categoria || null;
  isVisible.value = true;
};

onMounted(() => {
  log('DespesasCategoriasModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.despesas-categorias.open', handleOpen);
});

onBeforeUnmount(() => {
  log('DespesasCategoriasModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.despesas-categorias.open', handleOpen);
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.input-group-addon {
  background-color: #f4f4f4;
  border: 1px solid #d2d6de;
  border-left: 0;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #555;
  text-align: center;
}
</style>
